/**
 * O lançamento contínuo de pinout — a tela onde o dia inteiro acontece.
 *
 * O que ela precisa resolver, e que a de hoje não resolve bem: *"tem horas que
 * eles fazem duzentas inserções simultâneas"*. Por isso tudo aqui é teclado, e o
 * que se repete fica preenchido com o alfinete à vista.
 *
 * O contrato de teclas, igual em toda tela de lançamento (regra 3):
 *   ↵         escolhe na lista e anda para o próximo campo
 *   Ctrl ↵    grava o lote
 *   Ctrl D    mais uma ligação no mesmo circuito
 *   Esc       fecha a lista aberta
 */

import { useEffect, useMemo, useReducer, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "@/api/cliente"
import { conferirDuplicata } from "@/api/pinouts"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { chaves } from "@/api/query"
import {
  usarChicotes, usarComponentes, usarConectores, usarCores, usarRestricoes,
  type Catalogo, type ItemDeCatalogo,
} from "@/api/catalogos"
import { usarCircuitos } from "@/api/circuitos"
import type { CircuitoResponse, IncluirPinoutsResponse, PinoutResponse, PinoutUpsertItem } from "@/api/tipos"
import { CampoDeBusca, idDe, textoDe, VAZIO, type ValorBusca } from "@/kit/CampoDeBusca"
import { usarCadeiaDeFoco } from "@/kit/cadeiaDeFoco"
import { ORDEM_TIPOS_CIRCUITO, TIPOS_PINOUT } from "../tipos"
import {
  aplicarTrocas, estadoDeEdicao, estadoInicial, filaDeDuplicacao, pendencias, quantasLigacoes, reduzir,
  registrarTrocas, type CampoFixavel, type EstadoLancamento, type Trocas,
} from "./estado"

const SEM_TROCAS: Trocas = { componentes: new Map(), conectores: new Map() }

export function Lancamento({ projetoId, aoFechar, pinout, duplicar, aoGravar }: {
  projetoId: number
  aoFechar: () => void
  /** Presente = editando esse pinout. Ausente = lançando novo. */
  pinout?: PinoutResponse | null
  /**
   * Presente = duplicando estes pinouts: o formulário abre preenchido com eles,
   * um circuito por vez, e grava como pinouts novos.
   */
  duplicar?: PinoutResponse[] | null
  /**
   * Chamado só quando gravou de verdade. Quem hospeda precisa distinguir
   * "gravou" de "fechou": fechar no Esc não alterou nada, e avisar que alterou
   * ensina a ignorar o aviso.
   */
  aoGravar?: () => void
}) {
  const fila = useMemo(() => (duplicar && duplicar.length > 0 ? filaDeDuplicacao(duplicar) : []), [duplicar])
  const duplicando = fila.length > 0
  const [passo, setPasso] = useState(0)
  const trocas = useRef<Trocas>(SEM_TROCAS)

  const [e, despachar] = useReducer(
    reduzir, undefined,
    () => (pinout ? estadoDeEdicao(pinout) : fila[0] ? fila[0].estado : estadoInicial()))
  const editando = e.editando != null

  // Trocar de linha (ou de lote a duplicar) sem fechar o painel recarrega o formulário.
  useEffect(() => {
    setPasso(0)
    trocas.current = SEM_TROCAS
    despachar({
      tipo: "carregar",
      estado: pinout ? estadoDeEdicao(pinout) : fila[0] ? fila[0].estado : estadoInicial(),
    })
  }, [pinout, fila])

  /** Vai para o próximo circuito da duplicação, já com as trocas feitas até aqui; no fim, fecha. */
  function proximoPasso() {
    const n = passo + 1
    if (n >= fila.length) { aoFechar(); return }
    setPasso(n)
    despachar({ tipo: "carregar", estado: aplicarTrocas(fila[n]!, trocas.current) })
    setTimeout(() => cadeia.ir("circuito"), 0)
  }
  const qc = useQueryClient()
  const primeiroCampo = useRef<HTMLDivElement>(null)

  /**
   * A ordem do Enter, declarada. É a mesma do `EnterProximoCampo` do legado,
   * adaptada ao formato de hoje — cabeçalho primeiro, depois as ligações. Cada
   * ligação registra três passos, então a corrente cresce sozinha quando alguém
   * aperta Ctrl+D.
   */
  const ordemDoFoco = useMemo(
    () => [
      "circuito", "cor", "chicote", "restricao", "obs", "arquivo",
      ...e.ligacoes.flatMap((l) => [`comp-${l.chave}`, `con-${l.chave}`, `pino-${l.chave}`, `tipo-${l.chave}`]),
      "gravar",
    ],
    [e.ligacoes],
  )
  const cadeia = usarCadeiaDeFoco(ordemDoFoco)

  /**
   * Abrir o formulário leva o foco para o primeiro campo.
   *
   * Não é conforto: é o que faz o **Esc funcionar**. O `onKeyDown` mora na
   * `<section>` do formulário, então tecla só chega nele se o foco estiver
   * dentro. Abrindo pelo painel do gráfico, o foco ficava no botão da gaveta —
   * fora da seção — e o Esc não fechava nada. Pela grade o sintoma era o mesmo,
   * só que ninguém tinha esbarrado: o foco continuava na linha.
   *
   * O `setTimeout(0)` espera os campos se registrarem na cadeia, que acontece
   * no efeito de layout deles, depois deste render.
   *
   * Depende de `cadeia.ir`, que é estável, e não de `cadeia`: a cadeia muda a
   * cada ligação digitada (a ordem inclui os campos das ligações), e com ela na
   * lista o efeito rodava a cada tecla e arrancava o foco de volta para o
   * circuito — quem continuava digitando sem olhar sobrescrevia o circuito.
   */
  const irPara = cadeia.ir
  useEffect(() => {
    const t = setTimeout(() => irPara("circuito"), 0)
    return () => clearTimeout(t)
  }, [pinout, irPara])

  const componentes = usarComponentes(projetoId)
  const cores = usarCores(projetoId)
  const chicotes = usarChicotes(projetoId)
  const restricoes = usarRestricoes(projetoId)
  const circuitos = usarCircuitos(projetoId)

  // O tipo que o circuito escolhido já tem — é o que o select mostra enquanto a
  // pessoa não troca. Circuito novo não tem: começa em vazio (0).
  const circuitoId = e.cabecalho.circuito.estado === "vinculado" ? idDe(e.cabecalho.circuito) : 0
  const circuitoAtual = useQuery({
    queryKey: ["circuito", circuitoId] as const,
    enabled: circuitoId > 0,
    staleTime: 60_000,
    queryFn: () => obter<CircuitoResponse>(`api/circuitos/${circuitoId}`, {}),
  })
  const tipoDoCircuito = e.cabecalho.circuitoTipo ?? (circuitoId > 0 ? circuitoAtual.data?.tipo ?? 0 : 0)
  // Só vai para a API o tipo que a pessoa escolheu. Circuito que nasce sem tipo
  // escolhido nasce com 0 lá mesmo. Mandar 0 para todo "novo" zerava o tipo de um
  // circuito que já existia e chegou aqui como novo (digitado antes de a busca
  // responder, por exemplo): a API acha pelo nome e aplica o tipo.
  const circuitoTipoParaGravar = (est: EstadoLancamento) => est.cabecalho.circuitoTipo ?? undefined

  const faltando = pendencias(e)
  const quantas = quantasLigacoes(e)

  const gravar = useMutation({
    mutationFn: async (est: EstadoLancamento) => {
      const pinouts: PinoutUpsertItem[] = est.ligacoes
        .filter((l) => l.componente.estado !== "vazio" && l.pino.trim() !== "")
        .map((l) => ({
          // Atenção: `id` é o PROJETO. O pinout é `pinoutId`, e 0 cria.
          id: projetoId,
          // Com id, a API atualiza no lugar (o mesmo `pdb.alterar` do legado) e
          // confere duplicidade ignorando a própria linha. Com 0, cria.
          pinoutId: est.editando ?? 0,
          componenteId: idDe(l.componente),
          // O componente é resolvido por CÓDIGO (`ObterPorCodigoAsync`), não por
          // descrição — é o código que identifica (E004, M01) e é o que a pessoa
          // digita. Mandar código vazio criaria componente sem código e, pior, o
          // próximo lançamento casaria com esse vazio e reusaria o componente
          // errado calado. A descrição vai igual para a linha não nascer sem
          // nome na lista; quem quiser detalhar, detalha em Cadastros.
          componenteCodigo: l.componente.estado === "novo" ? l.componente.texto : "",
          componenteDescricao: l.componente.estado === "novo" ? l.componente.texto : "",
          conectorId: idDe(l.conector),
          conectorDescricao: l.conector.estado === "novo" ? l.conector.texto : "",
          circuitoId: idDe(est.cabecalho.circuito),
          circuitoDescricao: est.cabecalho.circuito.estado === "novo" ? est.cabecalho.circuito.texto : "",
          circuitoTipo: circuitoTipoParaGravar(est),
          corId: idDe(est.cabecalho.cor),
          corDescricao: est.cabecalho.cor.estado === "novo" ? est.cabecalho.cor.texto : "",
          chicoteId: idDe(est.cabecalho.chicote),
          chicoteDescricao: est.cabecalho.chicote.estado === "novo" ? est.cabecalho.chicote.texto : "",
          restricaoId: idDe(est.cabecalho.restricao),
          restricaoDescricao: est.cabecalho.restricao.estado === "novo" ? est.cabecalho.restricao.texto : "",
          pino: l.pino.trim(),
          obs: est.cabecalho.obs,
          arquivoOriginal: est.cabecalho.arquivoOriginal,
          tipo: l.tipo,
        }))

      const r = await api.post<IncluirPinoutsResponse>("api/pinouts", { pinouts })
      return r.data
    },
    onSuccess: (r, est) => {
      aoGravar?.()
      despachar({
        tipo: "gravou",
        idsCriados: r.idsCriados ?? [],
        criados: r.criados,
        ignorados: r.ignorados,
        mensagens: r.mensagens ?? [],
      })
      // A grade abaixo tem que mostrar as linhas novas sem recarregar a tela.
      qc.invalidateQueries({ queryKey: ["pinouts", projetoId] })
      qc.invalidateQueries({ queryKey: ["catalogo"] })
      // O tipo do circuito pode ter mudado aqui mesmo.
      qc.invalidateQueries({ queryKey: ["circuito"] })
      qc.invalidateQueries({ queryKey: chaves.projeto(projetoId) })
      // Editar tem fim: salvou, fecha. Lançar não — o cursor volta para o
      // primeiro campo do próximo circuito, que é o "nada de salvar e voltar"
      // da regra 10.
      if (editando) aoFechar()
      else if (duplicando) {
        // Linha recusada (duplicada, incompleta) fica na tela com o motivo; o resto segue.
        if (r.ignorados > 0) return
        trocas.current = registrarTrocas(trocas.current, fila[passo]!, est.ligacoes)
        proximoPasso()
      }
      else cadeia.ir("circuito")
    },
  })

  const desfazer = useMutation({
    mutationFn: async (ids: number[]) => { await api.post("api/pinouts/excluir-selecionados", { ids }, noProjeto(projetoId)) },
    onSuccess: () => {
      despachar({ tipo: "desfez" })
      qc.invalidateQueries({ queryKey: ["pinouts", projetoId] })
    },
  })

  // O estado de agora, para o Ctrl+Enter que grava no tique seguinte (ver abaixo).
  const atual = useRef(e)
  atual.current = e

  function aoTeclar(ev: React.KeyboardEvent) {
    if (ev.key === "Enter" && (ev.ctrlKey || ev.metaKey)) {
      ev.preventDefault()
      // Com o foco num campo de busca, esta mesma tecla confirma o que foi
      // digitado nele — e esse valor só existe no render seguinte. Gravar já
      // aqui levaria o formulário sem ele (o conector digitado e não gravado).
      const pendente = gravar.isPending
      setTimeout(() => {
        if (pendencias(atual.current).length === 0 && !pendente) gravar.mutate(atual.current)
      }, 0)
    }
    if (ev.key.toLowerCase() === "d" && (ev.ctrlKey || ev.metaKey)) {
      ev.preventDefault()
      despachar({ tipo: "adicionarLigacao" })
    }
    // Esc fecha — mas só na edição, que tem fim. No lançamento contínuo a tela
    // fica aberta de propósito, e fechá-la por engano custaria os alfinetes.
    if (ev.key === "Escape" && editando) {
      ev.preventDefault()
      aoFechar()
    }
  }

  /**
   * Aviso de duplicata **antes** de gravar.
   *
   * A inclusão em lote já recusa duplicata (RN-04) e devolve "Pinout já
   * cadastrado" — mas depois de a pessoa montar as duas ligações e apertar
   * Ctrl+Enter. Este é o mesmo aviso enquanto ela ainda está preenchendo.
   *
   * Só pergunta quando os quatro campos que definem a duplicidade já têm id: o
   * `conferencia` casa componente + conector + circuito + pino, e perguntar com
   * campo pela metade seria uma ida ao banco que não responde nada — e o banco
   * está no limite (regra 2).
   */
  const [jaExiste, setJaExiste] = useState<number[]>([])
  const primeira = e.ligacoes[0]
  const chaveDaConferencia = [
    idDe(e.cabecalho.circuito), idDe(primeira?.componente ?? VAZIO),
    idDe(primeira?.conector ?? VAZIO), (primeira?.pino ?? "").trim(),
  ].join("|")

  useEffect(() => {
    const [circuitoId, componenteId, conectorId, pino] = chaveDaConferencia.split("|")
    if (!Number(circuitoId) || !Number(componenteId) || !Number(conectorId) || !pino) {
      setJaExiste([])
      return
    }
    let vivo = true
    // Espera a digitação parar: sem isto, cada tecla no campo do pino vira uma
    // consulta.
    const t = setTimeout(() => {
      conferirDuplicata(
        projetoId, Number(componenteId), Number(conectorId), Number(circuitoId), pino,
        e.editando ?? 0,
      )
        .then((r) => { if (vivo) setJaExiste(r.map((x) => x.id)) })
        .catch(() => { if (vivo) setJaExiste([]) })
    }, 450)
    return () => { vivo = false; clearTimeout(t) }
  }, [chaveDaConferencia, projetoId, e.editando])

  const lote = e.ultimoLote
  const podeDesfazer = lote != null && lote.idsCriados.length > 0 && Date.now() - lote.quando < 10 * 60_000

  return (
    <section
      onKeyDown={aoTeclar}
      style={{
        flex: "0 0 auto", background: "var(--surface)", borderBottom: "1px solid var(--line2)",
        display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 18px 0" }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          {editando ? "Editar pinout" : duplicando ? "Duplicar pinouts" : "Lançar pinout"}
        </span>
        <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
          {editando
            ? <>linha <span className="num">{e.editando}</span> · Esc fecha sem salvar</>
            : duplicando
              ? <>circuito <b className="num">{passo + 1}</b> de <span className="num">{fila.length}</span>
                  {" · "}mude o que for diferente e grave — trocar o componente ou o conector aqui já vale
                  para os próximos circuitos</>
              : "um circuito, quantas ligações precisar"}
        </span>
        {duplicando && (
          <button onClick={proximoPasso}
                  title="Não grava este circuito e passa para o próximo"
                  style={{ fontSize: 11.5, color: "var(--brand-on-soft)", fontWeight: 600 }}>
            {passo + 1 < fila.length ? "pular este circuito" : "pular e terminar"}
          </button>
        )}
        <button onClick={aoFechar} style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--txt3)" }}>
          fechar
        </button>
      </div>

      {/* ------------------------------------------------- 1 · o circuito */}
      <div style={{ padding: "8px 18px 0" }}>
        <Etapa numero={1} titulo="O circuito" />
        <div className="grade-formulario" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 8 }}>
          <Campo rotulo="Circuito" obrigatorio refCampo={primeiroCampo}>
            <CampoDeBusca
              rotulo="Circuito"
              mono
              dica="1BRPT1.E"
              refCampo={cadeia.registrar("circuito")}
              aoAndar={() => cadeia.andar("circuito")}
              valor={e.cabecalho.circuito}
              aoMudar={(v) => {
                despachar({ tipo: "cabecalho", campo: "circuito", valor: v })
              }}
              fonte={{
                modo: "servidor",
                itens: circuitos.itens,
                carregando: circuitos.carregando,
                aoBuscar: circuitos.buscar,
                // O preenche vem da API: escolheu o circuito, cor e tipo entram
                // sozinhos. É o agregado que veio junto do `qtd`.
                preenche: (i: ItemDeCatalogo) => {
                  const c = circuitos.habituais(i.id)
                  if (c?.corId) {
                    despachar({
                      tipo: "cabecalho", campo: "cor",
                      valor: { estado: "vinculado", id: c.corId, texto: c.cor },
                    })
                  }
                },
              }}
            />
          </Campo>

          <Campo rotulo="Tipo do circuito">
            <select
              value={tipoDoCircuito}
              onChange={(ev) => despachar({ tipo: "circuitoTipo", valor: Number(ev.target.value) })}
              disabled={e.cabecalho.circuito.estado === "vazio"}
              aria-label="Tipo do circuito"
              title={e.cabecalho.circuito.estado === "vinculado"
                ? "Vale para o circuito, em todos os pinouts dele — é o mesmo campo de Cadastros → Circuitos."
                : "O circuito novo nasce com este tipo."}
              className="campo"
              style={{
                width: "100%", padding: "0 8px", borderRadius: 7, border: "1px solid var(--line2)",
                background: "var(--surface)", fontSize: 12.5,
              }}
            >
              {ORDEM_TIPOS_CIRCUITO.map(([v, r]) => <option key={v} value={v}>{r || "—"}</option>)}
            </select>
          </Campo>

          <Campo rotulo="Cor">
            <CampoDeBusca rotulo="Cor" mono dica="BR/PT" valor={e.cabecalho.cor}
              refCampo={cadeia.registrar("cor")} aoAndar={() => cadeia.andar("cor")}
              aoMudar={(v) => despachar({ tipo: "cabecalho", campo: "cor", valor: v })}
              fonte={emMemoria(cores)} />
          </Campo>

          <Campo rotulo="Chicote" alfinete="chicote" fixos={e.fixos} despachar={despachar}>
            <CampoDeBusca rotulo="Chicote" valor={e.cabecalho.chicote}
              refCampo={cadeia.registrar("chicote")} aoAndar={() => cadeia.andar("chicote")}
              aoMudar={(v) => despachar({ tipo: "cabecalho", campo: "chicote", valor: v })}
              fonte={emMemoria(chicotes)} />
          </Campo>

          <Campo rotulo="Restrição" alfinete="restricao" fixos={e.fixos} despachar={despachar}>
            <CampoDeBusca rotulo="Restrição" valor={e.cabecalho.restricao}
              refCampo={cadeia.registrar("restricao")} aoAndar={() => cadeia.andar("restricao")}
              aoMudar={(v) => despachar({ tipo: "cabecalho", campo: "restricao", valor: v })}
              fonte={emMemoria(restricoes)} />
          </Campo>

          <Campo rotulo="Observação">
            <Texto rotulo="Observação" valor={e.cabecalho.obs}
              aoMudar={(v) => despachar({ tipo: "cabecalho", campo: "obs", valor: v })}
              refCampo={cadeia.registrar("obs")} aoAndar={() => cadeia.andar("obs")} />
          </Campo>

          <Campo rotulo="Fonte" alfinete="arquivoOriginal" fixos={e.fixos} despachar={despachar}>
            <Texto mono rotulo="Fonte" valor={e.cabecalho.arquivoOriginal}
              aoMudar={(v) => despachar({ tipo: "cabecalho", campo: "arquivoOriginal", valor: v })}
              refCampo={cadeia.registrar("arquivo")} aoAndar={() => cadeia.andar("arquivo")} />
          </Campo>
        </div>

        {e.cabecalho.circuito.estado !== "vazio" && (
          <TrilhaDoCircuito texto={textoDe(e.cabecalho.circuito)} />
        )}
      </div>

      {/* ------------------------------------------------- 2 · as ligações */}
      <div style={{ padding: "10px 18px 0" }}>
        <Etapa numero={2} titulo="As ligações" />

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {e.ligacoes.map((l, i) => (
            <div key={l.chave} style={{ display: "grid", gridTemplateColumns: "22px 1.6fr 1fr 90px 1.2fr 26px", gap: 8, alignItems: "center" }}>
              <span style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--txt3)", textAlign: "right" }}>
                {i + 1}
              </span>

              <CampoDeBusca
                rotulo={`Componente da ligação ${i + 1}`} dica="componente" id={`comp-${l.chave}`}
                refCampo={cadeia.registrar(`comp-${l.chave}`)}
                aoAndar={() => cadeia.andar(`comp-${l.chave}`)}
                valor={l.componente}
                aoMudar={(v) => despachar({ tipo: "ligacao", chave: l.chave, campo: "componente", valor: v })}
                fonte={emMemoria(componentes)}
              />
              <ConectorDaLigacao
                projetoId={projetoId} componente={l.componente}
                rotulo={`Conector da ligação ${i + 1}`} id={`con-${l.chave}`}
                refCampo={cadeia.registrar(`con-${l.chave}`)}
                aoAndar={() => cadeia.andar(`con-${l.chave}`)}
                valor={l.conector}
                aoMudar={(v) => despachar({ tipo: "ligacao", chave: l.chave, campo: "conector", valor: v })}
              />
              <Texto
                mono dica="A01" rotulo={`Pino da ligação ${i + 1}`}
                valor={l.pino}
                refCampo={cadeia.registrar(`pino-${l.chave}`)}
                aoAndar={() => cadeia.andar(`pino-${l.chave}`)}
                aoMudar={(v) => despachar({ tipo: "ligacao", chave: l.chave, campo: "pino", valor: v })}
              />
              <select
                value={l.tipo}
                ref={cadeia.registrar(`tipo-${l.chave}`)}
                onChange={(ev) => despachar({ tipo: "ligacao", chave: l.chave, campo: "tipo", valor: Number(ev.target.value) })}
                onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); cadeia.andar(`tipo-${l.chave}`) } }}
                aria-label={`Tipo do pino da ligação ${i + 1}`}
                className="campo"
                style={{
                  padding: "0 8px", borderRadius: 7, border: "1px solid var(--line2)",
                  background: "var(--surface)", fontSize: 12.5,
                }}
              >
                {Object.entries(TIPOS_PINOUT).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
              </select>

              <button
                onClick={() => despachar({ tipo: "removerLigacao", chave: l.chave })}
                aria-label={`Remover a ligação ${i + 1}`}
                style={{ display: "flex", justifyContent: "center", color: "var(--txt3)" }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"
                     style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }}>
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          {!editando && (<><button
            onClick={() => despachar({ tipo: "adicionarLigacao" })}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px",
              borderRadius: 7, border: "1px dashed var(--line2)", fontSize: 12, color: "var(--txt2)",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
                 style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round" }}>
              <path d="M12 5.5v13M5.5 12h13" />
            </svg>
            Mais uma ligação
            <kbd style={{ fontSize: 10, color: "var(--txt3)" }}>Ctrl D</kbd>
          </button>

          <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
            Um circuito costuma ter duas pontas — de onde sai e onde chega.
          </span></>)}
        </div>
      </div>

      {/* ------------------------------------------------- gravar */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10, margin: "12px 0 0",
          padding: "10px 18px", borderTop: "1px solid var(--line)", background: "var(--surface2)",
        }}
      >
        {!editando && (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--txt2)" }}>
            <input
              type="checkbox"
              checked={e.continuar}
              onChange={(ev) => despachar({ tipo: "continuar", valor: ev.target.checked })}
            />
            Continuar cadastrando
          </label>
        )}

        {faltando.length > 0 && (
          <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>{faltando[0]!.texto}</span>
        )}

        {jaExiste.length > 0 && faltando.length === 0 && (
          <span
            title="A gravação vai recusar esta linha como duplicada (RN-04)."
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, height: 24, padding: "0 10px",
              borderRadius: 999, background: "var(--acao-soft)", color: "var(--acao-on-soft)",
              fontSize: 11.5, fontWeight: 600,
            }}
          >
            já existe — pinout <span className="num">{jaExiste[0]}</span>
            {jaExiste.length > 1 && <> e mais {jaExiste.length - 1}</>}
          </span>
        )}

        <span style={{ flex: 1 }} />

        {lote && <Resultado lote={lote} podeDesfazer={podeDesfazer}
                            desfazendo={desfazer.isPending}
                            aoDesfazer={() => desfazer.mutate(lote.idsCriados)} />}

        <button
          ref={cadeia.registrar("gravar")}
          onClick={() => gravar.mutate(e)}
          disabled={faltando.length > 0 || gravar.isPending}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 14px",
            borderRadius: 7, background: "var(--brand)", color: "#FFFFFF",
            fontSize: 12.5, fontWeight: 600,
            opacity: faltando.length > 0 || gravar.isPending ? 0.5 : 1,
          }}
        >
          {gravar.isPending
            ? (editando ? "Salvando…" : "Gravando…")
            : editando
              ? "Salvar alterações"
              : `Gravar ${quantas} ${quantas === 1 ? "ligação" : "ligações"}`}
          <kbd style={{ fontSize: 10, opacity: 0.85 }}>Ctrl ↵</kbd>
        </button>
      </div>

      {gravar.error != null && <ErroDoLote erro={gravar.error} />}
      {e.mensagens.length > 0 && <MensagensDoLote mensagens={e.mensagens} />}
    </section>
  )
}

// ------------------------------------------------------------------ pedaços

function emMemoria(c: Catalogo) {
  return { modo: "memoria" as const, itens: c.itens, carregando: c.carregando }
}

/**
 * O conector de uma ligação, escolhido entre os conectores **do componente dela**.
 *
 * A lista era uma só, a do componente da primeira ligação: na segunda (a outra
 * ponta do circuito) aparecia o conector do componente errado, e escolher ali
 * gravava a linha sem conector. O legado carregava a lista ao entrar no campo, a
 * partir do componente daquela linha — é o que isto faz. Componente novo ou
 * ainda vazio não tem conector cadastrado: a lista fica vazia e o digitado é criado.
 */
function ConectorDaLigacao({ projetoId, componente, rotulo, id, refCampo, aoAndar, valor, aoMudar }: {
  projetoId: number
  componente: ValorBusca
  rotulo: string
  id: string
  refCampo: (el: HTMLElement | null) => void
  aoAndar: () => void
  valor: ValorBusca
  aoMudar: (v: ValorBusca) => void
}) {
  const componenteId = idDe(componente)
  const conectores = usarConectores(componenteId > 0 ? projetoId : 0, componenteId || undefined)
  return (
    <CampoDeBusca
      rotulo={rotulo} mono dica="conector" id={id}
      refCampo={refCampo} aoAndar={aoAndar}
      valor={valor} aoMudar={aoMudar}
      fonte={{
        modo: "memoria",
        itens: componenteId > 0 ? conectores.itens : [],
        // Sem componente a consulta nem sai, e o "Buscando…" ficaria para sempre.
        carregando: componenteId > 0 && conectores.carregando,
      }}
    />
  )
}

function Etapa({ numero, titulo }: { numero: number; titulo: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, margin: "0 0 6px" }}>
      <span
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16,
          borderRadius: 4, background: "var(--brand-soft)", color: "var(--brand-on-soft)",
          fontFamily: "var(--fm)", fontSize: 10, fontWeight: 700,
        }}
      >
        {numero}
      </span>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>{titulo}</span>
    </div>
  )
}

function Campo({
  rotulo, obrigatorio, alfinete, fixos, despachar, refCampo, children,
}: {
  rotulo: string
  obrigatorio?: boolean
  alfinete?: CampoFixavel
  fixos?: Record<CampoFixavel, boolean>
  despachar?: React.Dispatch<{ tipo: "alfinete"; campo: CampoFixavel }>
  refCampo?: React.RefObject<HTMLDivElement>
  children: React.ReactNode
}) {
  const fixo = alfinete && fixos ? fixos[alfinete] : false
  return (
    <div ref={refCampo} style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 600, color: "var(--txt3)" }}>
        {rotulo}
        {obrigatorio && <span style={{ color: "var(--erro)" }}>*</span>}
        {alfinete && despachar && (
          // O alfinete é o comportamento que o sistema de hoje já tem escondido:
          // o que se repete fica preenchido. Aqui ele é visível e destravável.
          <button
            onClick={() => despachar({ tipo: "alfinete", campo: alfinete })}
            title={fixo ? "Fica preenchido depois de gravar. Clique para soltar." : "Limpa depois de gravar. Clique para fixar."}
            aria-pressed={fixo}
            style={{ marginLeft: "auto", display: "flex", color: fixo ? "var(--acao)" : "var(--txt3)", opacity: fixo ? 1 : 0.5 }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true"
                 style={{ fill: fixo ? "currentColor" : "none", stroke: "currentColor", strokeWidth: 1.9, strokeLinejoin: "round" }}>
              <path d="M9 3h6l-1 6 4 3v2H6v-2l4-3-1-6Z" /><path d="M12 14v7" />
            </svg>
          </button>
        )}
      </span>
      {children}
    </div>
  )
}

function Texto({ valor, aoMudar, mono, dica, refCampo, aoAndar, rotulo }: {
  valor: string
  aoMudar: (v: string) => void
  mono?: boolean
  dica?: string
  refCampo?: (el: HTMLElement | null) => void
  aoAndar?: () => void
  /** Sem isto o leitor de tela anuncia "campo de edição" e mais nada. */
  rotulo: string
}) {
  return (
    <input
      ref={refCampo}
      aria-label={rotulo}
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
      onKeyDown={(e) => {
        // Enter anda; Ctrl+Enter não, porque ali quem manda é o grava do lote.
        if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) { e.preventDefault(); aoAndar?.() }
      }}
      placeholder={dica}
      className="campo"
      style={{
        width: "100%", padding: "0 9px", borderRadius: 7,
        border: "1px solid var(--line2)", background: "var(--surface)", fontSize: 12.5,
        fontFamily: mono ? "var(--fm)" : undefined,
      }}
    />
  )
}

/**
 * O ponto separa o circuito do trecho, e trechos do mesmo fio cruzam chicotes
 * diferentes. Mostrar isso na hora evita a dúvida de "é o mesmo circuito?".
 */
function TrilhaDoCircuito({ texto }: { texto: string }) {
  const ponto = texto.indexOf(".")
  if (ponto < 0) return null
  const base = texto.slice(0, ponto)
  const trecho = texto.slice(ponto + 1)
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, marginTop: 7, padding: "6px 10px",
      borderRadius: 7, background: "var(--surface2)", fontSize: 11.5, color: "var(--txt2)",
    }}>
      <span style={{ fontFamily: "var(--fm)", fontWeight: 600, color: "var(--txt)" }}>{base}</span>
      <span style={{ color: "var(--txt3)" }}>é o circuito</span>
      <span style={{ width: 1, height: 12, background: "var(--line2)" }} />
      <span style={{ fontFamily: "var(--fm)", fontWeight: 600, color: "var(--brand-on-soft)" }}>.{trecho}</span>
      <span style={{ color: "var(--txt3)" }}>é o trecho — o mesmo fio noutro chicote</span>
    </div>
  )
}

function Resultado({ lote, podeDesfazer, desfazendo, aoDesfazer }: {
  lote: NonNullable<EstadoLancamento["ultimoLote"]>
  podeDesfazer: boolean
  desfazendo: boolean
  aoDesfazer: () => void
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12 }}>
      <span style={{ color: "var(--ok-on-soft)", fontWeight: 600 }}>
        {lote.criados} {lote.criados === 1 ? "gravada" : "gravadas"}
      </span>
      {lote.ignorados > 0 && (
        <span style={{ color: "var(--acao-on-soft)" }}>{lote.ignorados} ignoradas</span>
      )}
      {podeDesfazer && (
        <button onClick={aoDesfazer} disabled={desfazendo}
                style={{ fontSize: 12, fontWeight: 600, color: "var(--brand-on-soft)" }}>
          {desfazendo ? "desfazendo…" : "desfazer"}
        </button>
      )}
    </span>
  )
}

function MensagensDoLote({ mensagens }: { mensagens: EstadoLancamento["mensagens"] }) {
  return (
    <ul style={{
      margin: 0, padding: "8px 18px 10px", listStyle: "none",
      display: "flex", flexDirection: "column", gap: 4,
      background: "var(--acao-soft)", borderTop: "1px solid var(--acao)",
    }}>
      {mensagens.map((m, i) => (
        <li key={i} style={{ fontSize: 11.5, color: "var(--acao-on-soft)" }}>
          {/* O índice é a posição no array enviado, contada de zero — por isso o +1. */}
          <b style={{ fontFamily: "var(--fm)" }}>Ligação {m.indice + 1}</b>
          {m.campo && <span style={{ opacity: 0.8 }}> · {m.campo}</span>} — {m.texto}
        </li>
      ))}
    </ul>
  )
}

function ErroDoLote({ erro }: { erro: unknown }) {
  if (erro instanceof ErroDeTrava) {
    return (
      <div style={{
        padding: "10px 18px", fontSize: 11.5, lineHeight: 1.5,
        background: "var(--acao-soft)", color: "var(--acao-on-soft)", borderTop: "1px solid var(--acao)",
      }}>
        <b>Bloqueado pela trava do canário</b> — {erro.message}
      </div>
    )
  }
  return (
    <div style={{
      padding: "10px 18px", fontSize: 11.5,
      background: "var(--erro-soft)", color: "var(--erro-on-soft)", borderTop: "1px solid var(--erro)",
    }}>
      {erro instanceof ErroDaApi ? erro.message : "Não foi possível gravar."}
    </div>
  )
}
